import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { carrierService } from '../../services/carrierService';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const CarrierList = () => {
  const [carriers, setCarriers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    name: '',
    isActive: '',
    page: 1,
    limit: 10
  });

  useEffect(() => {
    console.log('Filters changed, fetching carriers:', filters);
    fetchCarriers();
  }, [filters]);

  // Debug: Log carriers state when it changes
  useEffect(() => {
    console.log('Carriers state updated:', carriers);
  }, [carriers]);

  const fetchCarriers = async () => {
    setIsLoading(true);
    try {
      const response = await carrierService.getCarriers(filters);
      console.log('API Response:', response);

      // API yanıtını kontrol et
      console.log('Full response:', response);

      // Örnek yanıtı kullanarak doğrudan taşıyıcıları ayarla
      // Bu, API yanıtının yapısını bildiğimizi varsayar
      if (response && response.success && response.data && response.data.rows) {
        // Doğrudan rows dizisini kullan
        const carrierData = response.data.rows;
        console.log('Setting carriers directly from rows:', carrierData);
        setCarriers(carrierData);

        // Sayfalama bilgilerini ayarla
        setPagination({
          currentPage: parseInt(filters.page),
          totalPages: Math.ceil(response.data.count / filters.limit),
          total: response.data.count,
          limit: parseInt(filters.limit)
        });
      } else {
        console.log('Invalid response structure');
        setCarriers([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          total: 0,
          limit: parseInt(filters.limit)
        });
      }
    } catch (error) {
      toast.error('Taşıyıcılar yüklenirken bir hata oluştu');
      console.error('Error fetching carriers:', error);
      setCarriers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Filtre değiştiğinde ilk sayfaya dön
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setFilters(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu taşıyıcıyı silmek istediğinize emin misiniz?')) {
      try {
        await carrierService.deleteCarrier(id);
        toast.success('Taşıyıcı başarıyla silindi');
        fetchCarriers();
      } catch (error) {
        toast.error('Taşıyıcı silinirken bir hata oluştu');
        console.error('Error deleting carrier:', error);
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await carrierService.toggleCarrierStatus(id, !currentStatus);
      toast.success(`Taşıyıcı durumu ${!currentStatus ? 'aktif' : 'pasif'} olarak güncellendi`);
      fetchCarriers();
    } catch (error) {
      toast.error('Taşıyıcı durumu güncellenirken bir hata oluştu');
      console.error('Error updating carrier status:', error);
    }
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Taşıyıcılar</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/carriers/create"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Yeni Taşıyıcı Ekle
          </Link>
        </div>
      </div>

      {/* Filtreler */}
      <div className="mt-4 bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Taşıyıcı Adı
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                name="name"
                id="name"
                className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="Taşıyıcı adı ara..."
                value={filters.name}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div>
            <label htmlFor="isActive" className="block text-sm font-medium text-gray-700">
              Durum
            </label>
            <select
              id="isActive"
              name="isActive"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.isActive}
              onChange={handleFilterChange}
            >
              <option value="">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => setFilters({ name: '', isActive: '', page: 1, limit: 10 })}
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Taşıyıcı Listesi */}
      <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : carriers.length === 0 ? (
          <div className="text-center p-12">
            <p className="text-gray-500">Taşıyıcı bulunamadı.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {console.log('Rendering carriers:', carriers)}
            {/* Hata ayıklama için taşıyıcıları doğrudan görüntüle 
            <li className="bg-yellow-100 p-4">
              <p className="font-bold">Debug: Taşıyıcılar</p>
              <pre className="text-xs overflow-auto max-h-40">{JSON.stringify(carriers, null, 2)}</pre>
            </li> */}
            {carriers.map((carrier) => (
              <li key={carrier.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-primary-600 truncate">{carrier.name}</p>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          carrier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {carrier.is_active ? 'Aktif' : 'Pasif'}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Link
                        to={`/carriers/${carrier.id}`}
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        <EyeIcon className="h-4 w-4" aria-hidden="true" />
                      </Link>
                      <Link
                        to={`/carriers/${carrier.id}/edit`}
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PencilIcon className="h-4 w-4" aria-hidden="true" />
                      </Link>
                      <button
                        type="button"
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        onClick={() => handleDelete(carrier.id)}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        Kod: {carrier.carrier_code}
                      </p>
                      {carrier.email && (
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          E-posta: {carrier.email}
                        </p>
                      )}
                      {carrier.phone && (
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          Telefon: {carrier.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Sayfalama */}
      {!isLoading && carriers.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Önceki
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Toplam <span className="font-medium">{pagination.total}</span> taşıyıcıdan{' '}
                <span className="font-medium">{(pagination.currentPage - 1) * pagination.limit + 1}</span>-
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.limit, pagination.total)}
                </span>{' '}
                arası gösteriliyor
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="sr-only">Önceki</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {/* Sayfa numaraları */}
                {[...Array(pagination.totalPages).keys()].map((page) => (
                  <button
                    key={page + 1}
                    onClick={() => handlePageChange(page + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border ${
                      pagination.currentPage === page + 1
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    } text-sm font-medium`}
                  >
                    {page + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="sr-only">Sonraki</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierList;
